import React from 'react';
import { motion } from 'framer-motion';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ColorPreview = ({ originalImage, colorResult, loading }) => {
  const downloadResult = () => {
    const textContent = `Hair Color Analysis Result\n\nColor: ${colorResult.color_name} (${colorResult.color_code})\n\n${colorResult.description}`;
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hair-color-${colorResult.color_code}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="w-full py-12 flex flex-col items-center justify-center space-y-4" data-testid="preview-loading">
        <Loader2 className="h-16 w-16 text-accent-primary animate-spin" />
        <p className="font-sans text-text-secondary">Creating your preview...</p>
      </div>
    );
  }

  if (!colorResult) {
    return (
      <div className="w-full py-12 text-center" data-testid="preview-empty">
        <p className="font-sans text-text-muted">Select a color to see the preview</p>
      </div>
    );
  }

  const hex = colorResult.hex || getHexFromCode(colorResult.color_code);

  return (
    <div className="w-full space-y-6" data-testid="color-preview">
      <div className="text-center mb-6">
        <h2 className="font-serif text-3xl md:text-4xl text-text-primary font-light mb-2">
          Your New Look
        </h2>
        <div className="flex items-center justify-center space-x-3">
          <div className="w-8 h-8 rounded-full border-2 border-border-color" style={{ backgroundColor: hex }} />
          <p className="font-sans text-lg text-accent-primary font-medium">
            {colorResult.color_name} ({colorResult.color_code})
          </p>
        </div>
      </div>

      {/* Side-by-Side Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Before */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-3"
          data-testid="before-image"
        >
          <div className="relative overflow-hidden rounded-sm border border-border-color">
            <img src={originalImage} alt="Before" className="w-full h-auto block" />
            <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 font-sans text-sm uppercase tracking-wider">
              Before
            </div>
          </div>
        </motion.div>

        {/* After — real color overlay using blend mode */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-3"
          data-testid="after-preview"
        >
          <div className="relative overflow-hidden rounded-sm border-2 border-accent-primary">
            <img src={originalImage} alt="After Preview" className="w-full h-auto block" />
            {/* color tint layer */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ backgroundColor: hex, mixBlendMode: 'color', opacity: 0.55 }}
            />
            {/* slight multiply for depth */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ backgroundColor: hex, mixBlendMode: 'multiply', opacity: 0.25 }}
            />
            <div className="absolute top-4 left-4 bg-accent-primary text-white px-4 py-2 font-sans text-sm uppercase tracking-wider">
              After Preview
            </div>
          </div>
        </motion.div>
      </div>

      {/* AI Description */}
      <div className="bg-bg-secondary border border-border-color p-6 rounded-sm" data-testid="color-description">
        <h3 className="font-sans text-sm text-text-muted uppercase tracking-wider mb-4">
          Professional Analysis
        </h3>
        <p className="font-sans text-text-primary leading-relaxed whitespace-pre-line">
          {colorResult.description}
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center">
        <Button
          onClick={downloadResult}
          className="bg-text-primary text-bg-primary px-8 py-4 font-sans text-sm tracking-widest uppercase hover:bg-accent-primary transition-colors"
          data-testid="download-result-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          DOWNLOAD RESULT
        </Button>
      </div>
    </div>
  );
};

// Lookup hex from chart data for the overlay
const SHADE_HEX_MAP = {
  '1N':'#1A1A1A','1A':'#131313','1B':'#0F0F15',
  '2N':'#2C1810','2A':'#261813',
  '3N':'#3B2414','3A':'#362418','3G':'#3F2710','3R':'#3D1C10','3M':'#3A1915',
  '4N':'#4A2F1A','4NN':'#4D3220','4A':'#453020','4G':'#523518','4C':'#4F2816','4R':'#4C2215','4RR':'#4A1E12','4RB':'#4B2418','4M':'#482320','4V':'#432628',
  '5N':'#5D3C26','5NN':'#614028','5A':'#583D2A','5G':'#674520','5C':'#6A3820','5R':'#65301E','5RR':'#622818','5RB':'#633528','5RV':'#5E2F30','5M':'#5C3232','5V':'#573338','5B':'#5A3828',
  '6N':'#7A5436','6NN':'#7E5838','6A':'#75553C','6G':'#856030','6C':'#8A4E28','6R':'#844428','6RR':'#803820','6RB':'#7D4A35','6M':'#784540','6V':'#734248','6W':'#885A35',
  '7N':'#996D48','7NN':'#9D7050','7A':'#947050','7G':'#A67940','7C':'#AC6338','7R':'#A55838','7RR':'#A04830','7M':'#985850','7V':'#935A58','7W':'#A87548','7WG':'#B07A45',
  '8N':'#B8885A','8NN':'#BD8D60','8A':'#B38B65','8G':'#C59450','8C':'#CC7F48','8R':'#C57548','8V':'#B37568','8P':'#BF9075','8W':'#C89060',
  '9N':'#D9A872','9A':'#D4AB7E','9G':'#E5B368','9P':'#E0B488','9V':'#D49888',
  '10N':'#F5D0A0','10A':'#F0D3AA','10G':'#FFD88C','10P':'#FAD8B0','10V':'#F0C0B0',
};

const getHexFromCode = (code) => SHADE_HEX_MAP[code?.toUpperCase()] || '#5D3C26';

export default ColorPreview;
